import {
  BannerNotification,
  BannerNotificationStyle,
} from '@cxc/react-fds-components';
import { useUniversalContext } from '@cxc/react-universal-components';
import { AppProviderContext } from 'commercial-sales-shared-component';
import React, { useContext, useMemo } from 'react';
import {
  AemJson,
  AemPage,
  AemStruct,
  StatusMessageReferenceType,
} from '../../constants/types/AemTypes';
import './Notification.scss';

export interface BaseNotificationProps {
  statusMessagesAPIName: AemPage;
  statusMessageKey: keyof AemStruct;
  visible?: boolean;
  code: string;
  notificationStyle: Extract<BannerNotificationStyle, 'success' | 'error'>;
  hideNotification: () => void;
}

export const Notification: React.FC<BaseNotificationProps> = ({
  statusMessagesAPIName,
  statusMessageKey,
  code,
  visible = false,
  hideNotification,
  notificationStyle,
}) => {
  const pagesToProvide = useContext(AppProviderContext)
    .pagesToProvide as unknown as AemJson;

  const { isLoggedIn } = useUniversalContext();

  const message = useMemo(() => {
    if (!code || !pagesToProvide) return '';

    const page = pagesToProvide[statusMessagesAPIName];
    const statusMessageList = page?.[
      statusMessageKey
    ] as StatusMessageReferenceType;

    return statusMessageList?.props.fieldReference.find(
      (statusMessage) => statusMessage.id === code
    )?.label;
  }, [code, pagesToProvide, statusMessageKey, statusMessagesAPIName]);

  const className = useMemo(() => {
    const visibleModifier = visible
      ? 'notification--visible'
      : 'notification--hidden';
    const loggedInModifier = isLoggedIn
      ? 'notification--authed'
      : 'notification--unauth';

    return ['notification', visibleModifier, loggedInModifier].join(' ');
  }, [isLoggedIn, visible]);

  const dataTestId = useMemo(
    () => `${notificationStyle}-notification`,
    [notificationStyle]
  );

  return (
    <div data-testid={dataTestId} className={className}>
      <BannerNotification
        visible={visible}
        notificationStyle={notificationStyle}
        message={message}
        onDismiss={hideNotification}
      />
    </div>
  );
};
